import React, { useState, useEffect } from 'react';
import axios from 'axios';

const AddActivity = () => {
    const [name, setName] = useState('');
    const [images, setImages] = useState([]);
    const [message, setMessage] = useState('');
    const [activities, setActivities] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editActivity, setEditActivity] = useState(null);
    const [editName, setEditName] = useState('');
    const [editImages, setEditImages] = useState([]);
    const [windowWidth, setWindowWidth] = useState(window.innerWidth);

    // Track window width for responsive design
    useEffect(() => {
        const handleResize = () => {
            setWindowWidth(window.innerWidth);
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Fetch activities on mount
    useEffect(() => {
        const fetchActivities = async () => {
            try {
                const response = await axios.get('https://marcsri.com/api/activities');
                setActivities(response.data);
            } catch (err) {
                setMessage('Error fetching activities: ' + err.message);
            } finally {
                setLoading(false);
            }
        };
        fetchActivities();
    }, []);

    const handleImageChange = (e) => {
        setImages([...e.target.files]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', name);
        images.forEach(image => formData.append('images', image));

        try {
            const response = await axios.post('https://marcsri.com/api/activities', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setMessage('Activity added successfully!');
            setName('');
            setImages([]);
            document.getElementById('images').value = ''; // Reset file input
            setActivities([response.data, ...activities]);
        } catch (err) {
            setMessage('Error adding activity: ' + err.message);
        }
    };

    const handleRemove = async (id) => {
        if (window.confirm('Are you sure you want to delete this activity?')) {
            try {
                await axios.delete(`https://marcsri.com/api/activities/${id}`);
                setActivities(activities.filter(activity => activity._id !== id));
                setMessage('Activity removed successfully!');
            } catch (err) {
                setMessage('Error removing activity: ' + err.message);
            }
        }
    };

    const openEditModal = (activity) => {
        setEditActivity(activity);
        setEditName(activity.name);
        setEditImages([]);
        setIsModalOpen(true);
    };

    const handleEditImageChange = (e) => {
        setEditImages([...e.target.files]);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('name', editName);
        editImages.forEach(image => formData.append('images', image));

        try {
            const response = await axios.put(`https://marcsri.com/api/activities/${editActivity._id}`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            setActivities(activities.map(activity => activity._id === editActivity._id ? response.data : activity));
            setMessage('Activity updated successfully!');
            setIsModalOpen(false);
            setEditActivity(null);
            setEditName('');
            setEditImages([]);
        } catch (err) {
            setMessage('Error updating activity: ' + err.message);
        }
    };

    // Responsive grid columns
    const getGridColumns = () => {
        if (windowWidth < 640) return 'grid-cols-1';       // Mobile portrait
        if (windowWidth < 1024) return 'grid-cols-2';      // Tablet portrait/landscape
        return 'grid-cols-3';                              // Desktop
    };

    return (
        <div className="min-h-screen bg-gradient-to-br from-indigo-100 to-gray-200 py-6 sm:py-8 lg:py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-5xl mx-auto">
                {/* Add Activity Form */}
                <div className="bg-white shadow-lg sm:shadow-2xl rounded-xl sm:rounded-2xl lg:rounded-3xl p-6 sm:p-8 lg:p-10 mb-8 sm:mb-12">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold sm:font-extrabold text-gray-900 mb-6 sm:mb-8 text-center">
                        Add New Activity
                    </h2>
                    <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                        <div>
                            <label htmlFor="name" className="block text-sm sm:text-base font-medium text-gray-700">
                                Activity Name
                            </label>
                            <input
                                type="text"
                                id="name"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                className="mt-1 sm:mt-2 block w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                required
                            />
                        </div>
                        <div>
                            <label htmlFor="images" className="block text-sm sm:text-base font-medium text-gray-700">
                                Activity Images
                            </label>
                            <input
                                type="file"
                                id="images"
                                multiple
                                accept="image/*"
                                onChange={handleImageChange}
                                className="mt-1 sm:mt-2 block w-full text-xs sm:text-sm text-gray-500 file:mr-2 sm:file:mr-4 file:py-2 sm:file:py-3 file:px-3 sm:file:px-6 file:rounded-lg sm:file:rounded-xl file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                            />
                        </div>
                        <button
                            type="submit"
                            className="w-full bg-indigo-600 text-white py-2 sm:py-3 px-4 rounded-lg sm:rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 font-medium sm:font-semibold"
                        >
                            Add Activity
                        </button>
                    </form>
                    {message && (
                        <p className={`mt-4 sm:mt-6 text-center text-xs sm:text-sm font-medium ${message.includes('Error') ? 'text-red-600' : 'text-green-600'}`}>
                            {message}
                        </p>
                    )}
                </div>

                {/* Display Activities */}
                <div className="bg-white shadow-lg sm:shadow-2xl rounded-xl sm:rounded-2xl lg:rounded-3xl p-6 sm:p-8 lg:p-10">
                    <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold sm:font-extrabold text-gray-900 mb-6 sm:mb-8 text-center">
                        Added Activities
                    </h2>
                    {loading ? (
                        <p className="text-center text-gray-500">Loading activities...</p>
                    ) : activities.length === 0 ? (
                        <p className="text-center text-gray-500">No activities added yet.</p>
                    ) : (
                        <div className={`grid ${getGridColumns()} gap-4 sm:gap-6 lg:gap-8`}>
                            {activities.map((activity) => (
                                <div
                                    key={activity._id}
                                    className="bg-gray-50 rounded-lg sm:rounded-xl shadow-md p-4 sm:p-6 hover:shadow-lg transition duration-300"
                                >
                                    <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-3 sm:mb-4 truncate">
                                        {activity.name}
                                    </h3>
                                    <div className="flex overflow-x-auto space-x-2 sm:space-x-3 pb-2 sm:pb-3">
                                        {activity.images && activity.images.length > 0 ? (
                                            activity.images.map((url, index) => (
                                                <img
                                                    key={index}
                                                    src={`https://marcsri.com${url}`}
                                                    alt={`${activity.name} ${index + 1}`}
                                                    className="w-12 h-12 sm:w-16 sm:h-16 object-cover rounded-md flex-shrink-0"
                                                    onError={(e) => { e.target.src = '/placeholder.jpg'; }}
                                                    loading="lazy"
                                                />
                                            ))
                                        ) : (
                                            <p className="text-gray-500 text-xs sm:text-sm">No images available</p>
                                        )}
                                    </div>
                                    <div className="flex space-x-2 sm:space-x-3 mt-3 sm:mt-4">
                                        <button
                                            onClick={() => openEditModal(activity)}
                                            className="flex-1 bg-blue-500 text-white py-1 sm:py-2 px-2 sm:px-4 rounded-lg sm:rounded-xl hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 text-xs sm:text-sm font-medium sm:font-semibold"
                                        >
                                            Update
                                        </button>
                                        <button
                                            onClick={() => handleRemove(activity._id)}
                                            className="flex-1 bg-red-500 text-white py-1 sm:py-2 px-2 sm:px-4 rounded-lg sm:rounded-xl hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 text-xs sm:text-sm font-medium sm:font-semibold"
                                        >
                                            Remove
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Update Modal */}
                {isModalOpen && (
                    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 sm:p-6">
                        <div className="bg-white rounded-lg sm:rounded-xl lg:rounded-2xl p-6 sm:p-8 w-full max-w-md">
                            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6 text-center">
                                Update Activity
                            </h3>
                            <form onSubmit={handleUpdate} className="space-y-4 sm:space-y-6">
                                <div>
                                    <label htmlFor="editName" className="block text-sm sm:text-base font-medium text-gray-700">
                                        Activity Name
                                    </label>
                                    <input
                                        type="text"
                                        id="editName"
                                        value={editName}
                                        onChange={(e) => setEditName(e.target.value)}
                                        className="mt-1 sm:mt-2 block w-full px-3 sm:px-4 py-2 sm:py-3 bg-gray-50 border border-gray-200 rounded-lg sm:rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                        required
                                    />
                                </div>
                                <div>
                                    <label htmlFor="editImages" className="block text-sm sm:text-base font-medium text-gray-700">
                                        Replace Images (optional)
                                    </label>
                                    <input
                                        type="file"
                                        id="editImages"
                                        multiple
                                        accept="image/*"
                                        onChange={handleEditImageChange}
                                        className="mt-1 sm:mt-2 block w-full text-xs sm:text-sm text-gray-500 file:mr-2 sm:file:mr-4 file:py-2 sm:file:py-3 file:px-3 sm:file:px-6 file:rounded-lg sm:file:rounded-xl file:border-0 file:text-xs sm:file:text-sm file:font-semibold file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                                    />
                                </div>
                                <div className="flex space-x-2 sm:space-x-3">
                                    <button
                                        type="submit"
                                        className="flex-1 bg-indigo-600 text-white py-2 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 text-sm sm:text-base font-medium sm:font-semibold"
                                    >
                                        Save Changes
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setIsModalOpen(false)}
                                        className="flex-1 bg-gray-500 text-white py-2 sm:py-3 px-3 sm:px-4 rounded-lg sm:rounded-xl hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 text-sm sm:text-base font-medium sm:font-semibold"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AddActivity;